import type { ScoreboardEvent } from './scoreboardSync'

const MARKER_EVENT_TYPES = [
  'goal',
  'period_start',
  'period_end',
  'game_end',
  'goalie_change',
  'offense',
]

// Seconds to offset before the event so the animation triggers on playback
export const MARKER_SEEK_OFFSET = 2

export interface VideoEventMarker {
  positionSec: number
  positionPercent: number
  eventType: string
  label: string
}

/**
 * Maps scoreboard events to seek-bar marker positions.
 * Only includes goals and period changes.
 *
 * @param scoreboardEvents - Sorted scoreboard events from the API
 * @param streamStartedAt - ISO timestamp of when the stream started
 * @param videoDurationSec - Total video duration in seconds
 * @returns Array of markers with position info
 */
export const computeVideoEventMarkers = (
  scoreboardEvents: ScoreboardEvent[] | null | undefined,
  streamStartedAt: string | null | undefined,
  videoDurationSec: number
): VideoEventMarker[] => {
  if (!scoreboardEvents || !streamStartedAt || videoDurationSec <= 0) {
    return []
  }

  const streamStartMs = new Date(streamStartedAt).getTime()
  const markers: VideoEventMarker[] = []

  for (const event of scoreboardEvents) {
    if (
      !event.occurred_at ||
      !event.event_type ||
      !MARKER_EVENT_TYPES.includes(event.event_type)
    ) {
      continue
    }

    const eventMs = new Date(event.occurred_at).getTime()
    const positionSec = (eventMs - streamStartMs) / 1000

    if (positionSec < 0 || positionSec > videoDurationSec) {
      continue
    }

    const positionPercent = (positionSec / videoDurationSec) * 100

    let label = ''
    if (event.event_type === 'goal') {
      label = `Goal${
        event.player?.full_name ? ': ' + event.player?.full_name : ''
      }`
    } else if (event.event_type === 'period_start') {
      label = event.period?.type?.name
        ? `${event.period.type.name} Start`
        : 'Period Start'
    } else if (event.event_type === 'period_end') {
      label = event.period?.type?.name
        ? `${event.period.type.name} End`
        : 'Period End'
    } else if (event.event_type === 'game_end') {
      label = 'Final'
    } else if (event.event_type === 'goalie_change') {
      label = 'Substitution'
    } else if (event.event_type === 'offense') {
      const offenseLine = [
        event.offense_type?.name,
        event.offense_severity?.name,
      ]
        .filter(Boolean)
        .join(', ')
      label = offenseLine ? `Foul: ${offenseLine}` : 'Foul'
    }

    markers.push({
      positionSec,
      positionPercent,
      eventType: event.event_type,
      label,
    })
  }

  return markers
}
